import { useState } from 'react'
import {
  ChevronDown, ChevronRight, TrendingUp, Plus, Edit2,
  CheckCircle, Clock, AlertTriangle
} from 'lucide-react'
import { Badge } from '../ui'

/**
 * ObiettivoCard — Card espandibile per un obiettivo PEP
 *
 * Props:
 *   obiettivo       - Oggetto obiettivo dal PEP
 *   pepId           - ID del PEP (per API calls)
 *   isCoord         - Boolean, coordinatore+
 *   isEducatore     - Boolean, ruolo educatore
 *   onAddVerifica   - callback(obId) — apre form verifica
 *   onEdit          - callback(obId) — apre form modifica
 *   onReload        - callback() — ricarica dati PEP
 */

const AREA_LABELS = {
  autonomia: 'Autonomia',
  socializzazione: 'Socializzazione',
  gestione_emotiva: 'Gestione emotiva',
  apprendimento: 'Apprendimento',
  relazioni_familiari: 'Relazioni familiari',
  comportamento: 'Comportamento',
  cura_di_se: 'Cura di sé',
  rispetto_regole: 'Rispetto regole',
  motivazione: 'Motivazione',
  altro: 'Altro'
}

const AREA_COLORS = {
  autonomia: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  socializzazione: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  gestione_emotiva: 'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  apprendimento: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  relazioni_familiari: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  comportamento: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cura_di_se: 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  rispetto_regole: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  motivazione: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  altro: 'bg-gray-50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
}

const PRIORITA_BAR = {
  alta: 'bg-red-500',
  media: 'bg-orange-400',
  bassa: 'bg-green-500'
}

const ESITO_VARIANT = {
  raggiunto: 'success',
  parziale: 'warning',
  non_raggiunto: 'error',
  ricalibrato: 'default',
  sospeso: 'default'
}

const ESITO_LABELS = {
  raggiunto: 'Raggiunto',
  parziale: 'Parziale',
  non_raggiunto: 'Non raggiunto',
  ricalibrato: 'Ricalibrato',
  sospeso: 'Sospeso'
}

const STATO_VARIANT = {
  attivo: 'default',
  raggiunto: 'success',
  parzialmente_raggiunto: 'warning',
  sospeso: 'default',
  chiuso: 'default'
}

export default function ObiettivoCard({
  obiettivo: ob,
  pepId,
  isCoord,
  isEducatore,
  onAddVerifica,
  onEdit,
  onReload
}) {
  const [expanded, setExpanded] = useState(false)

  const verifiche = ob.verifiche || []
  const ultimaVerifica = verifiche.length > 0
    ? verifiche[verifiche.length - 1]
    : (ob.ultimaVerifica || null)

  // Calcola progresso se indicatore automatico
  const { progresso, valoreAttuale } = calcProgresso(ob, ultimaVerifica)

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Barra priorità colorata a sinistra */}
      <div className="flex">
        <div className={`w-1 flex-shrink-0 ${PRIORITA_BAR[ob.priorita] || PRIORITA_BAR.media}`} />

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="p-3 sm:p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                {/* Area + Stato */}
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <span className={`text-[10px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 rounded-full ${AREA_COLORS[ob.area] || AREA_COLORS.altro}`}>
                    {AREA_LABELS[ob.area] || ob.areaCustom || ob.area}
                  </span>
                  {ob.stato !== 'attivo' && (
                    <Badge variant={STATO_VARIANT[ob.stato] || 'default'} className="text-[10px]">
                      {ob.stato.replace(/_/g, ' ')}
                    </Badge>
                  )}
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    ob.priorita === 'alta' ? 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20'
                    : ob.priorita === 'bassa' ? 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20'
                    : 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/20'
                  }`}>
                    {ob.priorita}
                  </span>
                </div>

                {/* Titolo */}
                <h4 className="font-medium text-gray-900 dark:text-white mt-1.5 text-sm leading-snug">
                  {ob.titolo}
                </h4>

                {/* Strategia */}
                {ob.strategia && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{ob.strategia}</p>
                )}
              </div>

              {/* Azioni coordinatore */}
              {isCoord && !['raggiunto', 'chiuso'].includes(ob.stato) && (
                <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                  {ob.stato === 'attivo' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onAddVerifica?.(ob._id) }}
                      className="p-1 sm:p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors"
                      title="Aggiungi verifica"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit?.(ob._id) }}
                    className="p-1 sm:p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    title="Modifica obiettivo"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Barra progresso (indicatore automatico) */}
            {progresso !== null && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-500 mb-1 gap-1">
                  <span className="hidden sm:inline">{ob.indicatore.valoreBaseline} {ob.indicatore.etichettaValore}</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {valoreAttuale} {ob.indicatore.etichettaValore} ({progresso}%)
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 flex-shrink-0" />
                    <span className="hidden sm:inline">{ob.indicatore.valoreTarget} {ob.indicatore.etichettaValore}</span>
                    <span className="sm:hidden">{ob.indicatore.valoreTarget}</span>
                  </span>
                </div>
                <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      progresso >= 80 ? 'bg-green-500' : progresso >= 40 ? 'bg-yellow-500' : 'bg-red-400'
                    }`}
                    style={{ width: `${progresso}%` }}
                  />
                </div>
              </div>
            )}

            {/* Indicatore qualitativo: mostra criterio + ultimo esito */}
            {ob.indicatore?.tipo === 'qualitativo' && (
              <div className="mt-3">
                {ob.indicatore.criterio && (
                  <p className="text-xs text-gray-400 italic mb-1">
                    Criterio: {ob.indicatore.criterio}
                  </p>
                )}
                {ultimaVerifica && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Ultimo esito:</span>
                    <Badge variant={ESITO_VARIANT[ultimaVerifica.esito] || 'default'} className="text-[10px]">
                      {ESITO_LABELS[ultimaVerifica.esito] || ultimaVerifica.esito}
                    </Badge>
                    <span className="text-[10px] text-gray-400">
                      {new Date(ultimaVerifica.dataEvento).toLocaleDateString('it-IT', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Footer: scadenza + toggle espansione */}
            <div className="flex items-center justify-between mt-3 gap-2">
              <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-gray-500 min-w-0">
                {ob.dataScadenzaVerifica && ob.stato === 'attivo' && (
                  <ScadenzaBadge dataScadenza={ob.dataScadenzaVerifica} />
                )}
                {verifiche.length > 0 && (
                  <span>{verifiche.length} verific{verifiche.length === 1 ? 'a' : 'he'}</span>
                )}
              </div>

              {/* Toggle storico (coordinatore o se ha verifiche) */}
              {(isCoord && verifiche.length > 0) && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {expanded ? 'Nascondi' : 'Storico verifiche'}
                  {expanded
                    ? <ChevronDown className="h-3.5 w-3.5" />
                    : <ChevronRight className="h-3.5 w-3.5" />
                  }
                </button>
              )}
            </div>
          </div>

          {/* Sezione espansa: storico verifiche (solo coordinatore) */}
          {expanded && isCoord && verifiche.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 bg-gray-50/50 dark:bg-gray-800/50">
              <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Storico verifiche
              </h5>
              <div className="space-y-2">
                {[...verifiche].reverse().map((v, idx) => (
                  <div key={v._id || idx} className="flex items-start gap-3 text-xs">
                    <div className="flex-shrink-0 mt-0.5">
                      {v.esito === 'raggiunto' ? (
                        <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                      ) : v.esito === 'non_raggiunto' ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                      ) : (
                        <Clock className="h-3.5 w-3.5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant={ESITO_VARIANT[v.esito] || 'default'} className="text-[10px]">
                          {ESITO_LABELS[v.esito] || v.esito}
                        </Badge>
                        <span className="text-gray-400">
                          {new Date(v.dataEvento).toLocaleDateString('it-IT', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </span>
                        {v.valoreQuantitativo != null && (
                          <span className="text-gray-500 font-medium">
                            {v.valoreQuantitativo} {v.etichettaValore || ob.indicatore?.etichettaValore || ''}
                          </span>
                        )}
                        {v.isDatoStorico && (
                          <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">
                            storico
                          </span>
                        )}
                      </div>
                      {v.note && (
                        <p className="text-gray-500 mt-0.5 line-clamp-2">{v.note}</p>
                      )}
                      {v.verificatoDaNome && (
                        <span className="text-[10px] text-gray-400">{v.verificatoDaNome}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────

function ScadenzaBadge({ dataScadenza }) {
  const giorni = Math.ceil(
    (new Date(dataScadenza) - new Date()) / (1000 * 60 * 60 * 24)
  )

  if (giorni < 0) {
    return (
      <span className="flex items-center gap-1 text-red-600 font-medium">
        <AlertTriangle className="h-3 w-3" />
        Scaduta da {Math.abs(giorni)}g
      </span>
    )
  }
  if (giorni <= 7) {
    return (
      <span className="flex items-center gap-1 text-orange-600 font-medium">
        <Clock className="h-3 w-3" />
        Scade tra {giorni}g
      </span>
    )
  }
  return (
    <span className="flex items-center gap-1">
      <Clock className="h-3 w-3" />
      Prossima: {new Date(dataScadenza).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
    </span>
  )
}

// ── Helpers ──────────────────────────────────────────────────

function calcProgresso(ob, ultimaVerifica) {
  if (ob.indicatore?.tipo !== 'automatico') return { progresso: null, valoreAttuale: null }
  if (ob.indicatore.valoreBaseline == null || ob.indicatore.valoreTarget == null) {
    return { progresso: null, valoreAttuale: null }
  }

  const base = ob.indicatore.valoreBaseline
  const target = ob.indicatore.valoreTarget
  const current = ultimaVerifica?.valoreQuantitativo ?? base

  if (target === base) return { progresso: null, valoreAttuale: current }

  const progresso = Math.max(0, Math.min(100, Math.round(
    ((current - base) / (target - base)) * 100
  )))

  return { progresso, valoreAttuale: current }
}
